import styled from 'styled-components';
import ArrowBackIcon from '@/assets/mo/ArrowBack.svg?react';
import ShareIcon from '@/assets/mo/share.svg?react';
import EyesIcon from '@/assets/mo/eyes.svg?react';
import CircleIcon from '@/assets/mo/circle.svg?react';
import ArrowRightIcon from '@/assets/mo/ArrowRight.svg?react';
import ReviewMetaTag from './ReviewMetaTag';
import { useParams } from 'react-router-dom';
import { fetchReviewDetail } from '@/api/review';
import { useQuery } from '@tanstack/react-query';
import TagBadge from './TagBadge';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet-async';
import type { ReviewDetail } from '@/api/review';

const ReviewDetail = () => {
  const isMobile = useMediaQuery({ maxWidth: 600 });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery<ReviewDetail>({
    queryKey: ['reviewDetail', id],
    queryFn: () => fetchReviewDetail(Number(id)),
    enabled: !!id,
  });

  if (isLoading) return <div>로딩 중...</div>;
  if (isError || !data) return <div>데이터를 불러올 수 없습니다.</div>;

  if (!isMobile) {
    return <div>🖥️ PC 버전 준비 중입니다</div>; // ✅ PC 버전이 없는 경우 처리
  }
  return (
    <>
      {/* ✅ 메타태그 삽입 */}
      <Helmet>
        <title>{`${data.title} | 올받음`}</title>
        <meta name="description" content={data.content.slice(0, 1000)} />
        <meta property="og:title" content={`${data.title} | 올받음`} />
        <meta property="og:description" content={data.content.slice(0, 1000)} />
      </Helmet>{' '}
      <HeaderWrapper>
        <LeftSection>
          <ArrowWrapper>
            <ArrowBackIcon onClick={() => navigate(`/review`)} />
          </ArrowWrapper>
          <ReviewText>후기</ReviewText>
        </LeftSection>
        <RightSection>
          <ShareWrapper>
            <ShareIcon
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  alert('링크가 복사되었습니다.');
                } catch (err) {
                  console.error('복사 실패:', err);
                }
              }}
            />
          </ShareWrapper>
        </RightSection>
      </HeaderWrapper>
      <ContentWrapper>
        <InnerSection>
          <MetaInfoRow>
            <DateText>
              {data.incidentDate ? new Date(data.incidentDate).toLocaleDateString('ko-KR') : ''}
            </DateText>
            <ViewCount>
              <EyesIcon />
              <ViewText>{data?.viewCount || '없음'}</ViewText>
            </ViewCount>
          </MetaInfoRow>
          <TitleBox>
            <TitleText>{data?.title || '없음'}</TitleText>
            <MetaRow>
              <MetaTagContainer>
                <ReviewMetaTag
                  label={data.issues[0]?.isPayoutApproved ? '지급' : '부지급'}
                  totalFee={
                    data.issues[0]?.isPayoutApproved && data.issues[0]?.totalFee !== undefined
                      ? String(data.issues[0].totalFee)
                      : undefined
                  }
                />
              </MetaTagContainer>
            </MetaRow>
          </TitleBox>
          <BodyText
            dangerouslySetInnerHTML={{
              __html: (data?.content || '없음').replace(/\n/g, '<br />'),
            }}
          />
          <TagContainer>
            <InnerTagWrap>
              {data?.tags?.map((tagItem) => (
                <TagBadge key={tagItem.id} label={`#${tagItem.tag}`} />
              ))}
            </InnerTagWrap>
          </TagContainer>
        </InnerSection>
        <Divider />
        {data.issues?.[0]?.issueTitle?.trim() || data.issues?.[0]?.issueContent?.trim() ? (
          <InnerSection>
            <SecondCardContainer>
              <SecondCardInner>
                <SecondTopSection>
                  {data.issues?.[0]?.adjuster?.profileImage ? (
                    <img
                      src={data.issues[0].adjuster.profileImage}
                      alt="손해사정사 프로필"
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        objectFit: 'scale-down',
                        imageRendering: 'auto',
                      }}
                    />
                  ) : (
                    <CircleIcon />
                  )}
                  <SecondTextWrapper>
                    <AdjusterInfoRow>
                      <AdjusterName>
                        {data.issues?.[0]?.adjuster?.name ?? '정보없음'} 손해사정사
                      </AdjusterName>
                      <ArrowRightIcon style={{ width: '18px', height: '18px' }} />
                    </AdjusterInfoRow>
                    <AdjusterMetaTagWrapper>
                      {data?.issues?.[0]?.tags?.map((tag, index) => (
                        <MetaTagBox key={index}>
                          <MetaTagText>{tag}</MetaTagText>
                        </MetaTagBox>
                      ))}
                    </AdjusterMetaTagWrapper>
                  </SecondTextWrapper>
                </SecondTopSection>
              </SecondCardInner>
            </SecondCardContainer>

            <ReviewTitle>{data.issues[0].issueTitle}</ReviewTitle>
            <ReviewBody>{data.issues[0].issueContent}</ReviewBody>
          </InnerSection>
        ) : (
          <div
            style={{
              padding: '16px',
              fontSize: '14px',
              color: '#6f6f6f',
              width: '100%',
              minHeight: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            등록된 답변이 없습니다.
          </div>
        )}
      </ContentWrapper>
    </>
  );
};

export default ReviewDetail;

const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  background: var(--Primary-white, #fff);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1 0 0;
`;

const ArrowWrapper = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ShareWrapper = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ReviewText = styled.div`
  display: flex;
  padding: 0px 16px;
  align-items: flex-end;
  gap: 10px;
  flex: 1 0 0;
  justify-content: flex-start;

  color: var(--gray-ui_gray02, #252525);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 19.2px */
  letter-spacing: -0.32px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 0 0;
`;

const ContentWrapper = styled.section`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const InnerSection = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  background: #fff;
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const TitleText = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray01, #191919);
  text-align: center;
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.44px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: flex-start; /* ✅ 왼쪽 정렬 */
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const DateText = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const ViewCount = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ViewText = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.24px;
`;

const BodyText = styled.div`
  align-self: stretch;
  text-align: start;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.28px;
`;

const MetaTagContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 16px 0 8px 0;
`;

const SecondCardContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  background: #fff;
`;

const SecondCardInner = styled.div`
  display: flex;
  padding: 12px 0px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const SecondTopSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SecondTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
`;

const AdjusterInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const AdjusterName = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.24px;
`;

const AdjusterMetaTagWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const MetaTagBox = styled.div`
  display: flex;
  padding: 4px 4px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: var(--Primary-lightgray, #f3f3f3);
`;

const MetaTagText = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.2px;
`;

const ReviewTitle = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray01, #191919);
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 30.8px */
  letter-spacing: -0.44px;
  padding-bottom: 24px;
  text-align: start;
  padding-top: 12px;
`;

const ReviewBody = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 188%; /* 26.32px */
  letter-spacing: -0.28px;
  text-align: start;
`;

const TagContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: #fff;
`;

const InnerTagWrap = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const Divider = styled.div`
  width: 100%;
  height: 20px;
  background: var(--Primary-lightgray, #f3f3f3);
`;

const MetaInfoRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 8px 0;
`;
